const CONSULTATION_FORM_SELECTOR = 'form#consultation-form';
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const SUBMISSION_TIMEOUT = 60000;
const ALLOWED_ATTACHMENT_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'video/mp4',
  'video/quicktime',
  'video/webm',
]);

function configValue(name, fallback) {
  return window.consultationConfig?.[name] || fallback;
}

function getConsultationApiUrl() {
  const configuredUrl = window.consultationConfig?.apiUrl?.trim();
  const isLocalPreview = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';
  if (isLocalPreview && (!configuredUrl || configuredUrl.includes('consultation-ingest.workers.dev'))) {
    return 'http://127.0.0.1:8787/v1/inquiries';
  }
  return configuredUrl || 'https://consultation-ingest.eleanoranco.workers.dev/v1/inquiries';
}

function addHiddenField(form, name, value) {
  let field = form.querySelector(`input[name="${name}"]`);

  if (!field) {
    field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    form.prepend(field);
  }

  field.value = value;
  return field;
}

function setConditionalSection(sectionId, isVisible) {
  const section = document.getElementById(sectionId);
  if (!section) return;

  section.classList.toggle('hidden', !isVisible);
  section.querySelectorAll('input, select, textarea').forEach((control) => {
    control.disabled = !isVisible;
  });
}

function getSelectedRadioValue(form, name) {
  return form.querySelector(`input[name="${name}"]:checked`)?.value || null;
}

function getMissingSelection(form) {
  const jewelleryType = getSelectedRadioValue(form, 'jewelryType');
  const stoneType = getSelectedRadioValue(form, 'stoneType');
  const requiredSelections = [
    ['jewelryType', 'Jewellery Type'],
    ['stoneType', 'Main Stone Type'],
    ['metal', 'Metal'],
  ];

  if (jewelleryType === 'Ring') requiredSelections.push(['ringStyle', 'Ring Style']);
  if (jewelleryType === 'Engagement Ring') requiredSelections.push(['engagementStyle', 'Engagement Ring Style']);
  if (stoneType === 'Diamond' || stoneType === 'Gemstone') {
    requiredSelections.push(['stoneOrigin', 'Stone Origin'], ['shape', 'Stone Shape']);
  }
  if (stoneType === 'Diamond') requiredSelections.push(['diamondColor', 'Diamond Color']);
  if (stoneType === 'Gemstone') requiredSelections.push(['gemstoneType', 'Gemstone Type']);
  if (stoneType === 'Engraved') requiredSelections.push(['engravedType', 'Engraved Stone Type']);

  return requiredSelections.find(([name]) => !getSelectedRadioValue(form, name));
}

function isInvalidAttachment(file) {
  return Boolean(file && (
    file.size > MAX_ATTACHMENT_SIZE
    || !ALLOWED_ATTACHMENT_TYPES.has(file.type.toLowerCase())
  ));
}

function showSuccessDialog(message) {
  document.querySelector('[data-consultation-success-dialog]')?.remove();

  const overlay = document.createElement('div');
  overlay.dataset.consultationSuccessDialog = '';
  overlay.className = 'consultation-success-dialog';
  overlay.setAttribute('role', 'presentation');

  const dialog = document.createElement('div');
  dialog.className = 'consultation-success-dialog__panel';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'consultation-success-title');

  const icon = document.createElement('div');
  icon.className = 'consultation-success-dialog__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '✓';

  const title = document.createElement('h2');
  title.id = 'consultation-success-title';
  title.textContent = 'Thank you';

  const copy = document.createElement('p');
  copy.textContent = message;

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'consultation-success-dialog__close';
  closeButton.textContent = 'Continue browsing';

  const close = () => {
    document.removeEventListener('keydown', onKeydown);
    document.body.classList.remove('consultation-dialog-open');
    overlay.remove();
  };
  const onKeydown = (event) => {
    if (event.key === 'Escape') close();
  };

  closeButton.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  document.addEventListener('keydown', onKeydown);

  dialog.append(icon, title, copy, closeButton);
  overlay.append(dialog);
  document.body.append(overlay);
  document.body.classList.add('consultation-dialog-open');
  requestAnimationFrame(() => overlay.classList.add('is-visible'));
  closeButton.focus();
}

function showFormMessage(form, message, type = 'error') {
  if (type === 'success') {
    form.querySelector('[data-consultation-form-message]')?.remove();
    showSuccessDialog(message);
    return;
  }

  let messageElement = form.querySelector('[data-consultation-form-message]');

  if (!messageElement) {
    messageElement = document.createElement('div');
    messageElement.dataset.consultationFormMessage = '';
    messageElement.setAttribute('role', 'alert');
    form.querySelector('button[type="submit"]')?.before(messageElement);
  }

  messageElement.textContent = message;
  messageElement.dataset.status = type;
  messageElement.hidden = false;
  messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function sectionForControl(form, name) {
  return form.querySelector(`[name="${name}"]`)?.closest('.section');
}

function decorateSection(section, className, step) {
  if (!section) return;
  section.classList.add(className);
  const heading = section.querySelector(':scope > h3');
  if (heading && step) heading.dataset.step = step;
}

function createUploadIcon() {
  const icon = document.createElement('span');
  icon.className = 'consultation-upload-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = '<svg viewBox="0 0 48 48" focusable="false"><path d="M16.5 35.5h-3.25A8.25 8.25 0 0 1 12 19.1a12 12 0 0 1 23.15 2.65A7 7 0 0 1 34.5 35.5h-3M24 16v22m-7-15 7-7 7 7"/></svg>';
  return icon;
}

function buildDetailsSection(form) {
  const fileInput = form.querySelector('input[type="file"][name="reference"], input[type="file"][name="attachment"]');
  const emailInput = form.querySelector('input[type="email"]');
  const notesInput = form.querySelector('textarea[name="notes"]');
  const section = fileInput?.closest('.section');
  if (!section || !fileInput || !emailInput || !notesInput) return null;

  section.classList.add('consultation-step', 'consultation-details');
  const heading = section.querySelector(':scope > h3') || document.createElement('h3');
  heading.textContent = 'Share Your Idea';
  heading.dataset.step = '04';

  fileInput.id = 'consultation-attachment';
  fileInput.classList.add('consultation-native-file');

  const uploadZone = document.createElement('div');
  uploadZone.className = 'consultation-upload-field';
  uploadZone.append(fileInput, createUploadIcon());

  const uploadTitle = document.createElement('h4');
  uploadTitle.textContent = 'Upload your reference';

  const uploadCopy = document.createElement('p');
  uploadCopy.textContent = 'Drag & drop an image or video, or';

  const uploadTrigger = document.createElement('label');
  uploadTrigger.className = 'consultation-upload-trigger';
  uploadTrigger.htmlFor = fileInput.id;
  uploadTrigger.textContent = 'Choose a file';

  const hint = document.createElement('small');
  hint.className = 'consultation-upload-hint';
  hint.textContent = 'JPG, PNG, WEBP, HEIC, MP4, MOV · up to 10 MB';

  const fileStatus = document.createElement('div');
  fileStatus.className = 'consultation-file-status';
  fileStatus.hidden = true;

  const fileName = document.createElement('span');
  fileName.className = 'consultation-file-name';

  const removeFile = document.createElement('button');
  removeFile.type = 'button';
  removeFile.className = 'consultation-file-remove';
  removeFile.textContent = 'Remove';
  removeFile.setAttribute('aria-label', 'Remove selected attachment');
  fileStatus.append(fileName, removeFile);

  uploadZone.append(uploadTitle, uploadCopy, uploadTrigger, hint, fileStatus);

  let nameInput = form.querySelector('input[name="name"], input[name="customer_name"]');
  if (!nameInput) {
    nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.name = 'name';
  }
  nameInput.id = nameInput.id || 'consultation-name';
  nameInput.required = true;
  nameInput.autocomplete = 'name';
  nameInput.placeholder = 'Your name';
  const nameField = document.createElement('div');
  nameField.className = 'consultation-field';
  const nameLabel = document.createElement('label');
  nameLabel.htmlFor = nameInput.id;
  nameLabel.textContent = 'Your name';
  nameField.append(nameLabel, nameInput);

  emailInput.id = emailInput.id || 'consultation-email';
  emailInput.autocomplete = 'email';
  const emailField = document.createElement('div');
  emailField.className = 'consultation-field';
  const emailLabel = document.createElement('label');
  emailLabel.htmlFor = emailInput.id;
  emailLabel.textContent = 'Email address';
  emailField.append(emailLabel, emailInput);

  notesInput.id = notesInput.id || 'consultation-notes';
  notesInput.placeholder = 'Tell us about the piece you have in mind, preferred details, timing, or anything else you would like us to know.';
  const notesField = document.createElement('div');
  notesField.className = 'consultation-field';
  const notesLabel = document.createElement('label');
  notesLabel.htmlFor = notesInput.id;
  notesLabel.textContent = 'Tell us about your idea';
  notesField.append(notesLabel, notesInput);

  section.replaceChildren(heading, uploadZone, nameField, emailField, notesField);

  removeFile.addEventListener('click', () => {
    fileInput.value = '';
    fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  ['dragenter', 'dragover'].forEach((eventName) => {
    uploadZone.addEventListener(eventName, (event) => {
      event.preventDefault();
      uploadZone.classList.add('is-dragging');
    });
  });
  ['dragleave', 'drop'].forEach((eventName) => {
    uploadZone.addEventListener(eventName, (event) => {
      event.preventDefault();
      uploadZone.classList.remove('is-dragging');
    });
  });
  uploadZone.addEventListener('drop', (event) => {
    const file = event.dataTransfer?.files?.[0];
    if (!file || typeof DataTransfer === 'undefined') return;
    const transfer = new DataTransfer();
    transfer.items.add(file);
    fileInput.files = transfer.files;
    fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  return { fileInput, uploadZone, hint, fileStatus, fileName };
}

function enhanceConsultationForm(form) {
  decorateSection(sectionForControl(form, 'jewelryType'), 'consultation-step', '01');
  decorateSection(sectionForControl(form, 'stoneType'), 'consultation-step', '02');
  decorateSection(sectionForControl(form, 'metal'), 'consultation-step', '03');

  [
    'ringStyleOptions',
    'engagementOptions',
    'stoneOriginOptions',
    'diamondOptions',
    'gemstoneOptions',
    'engravedOptions',
    'shapeOptions',
  ].forEach((id) => document.getElementById(id)?.classList.add('consultation-substep'));

  const submitButton = form.querySelector('button[type="submit"]');
  if (submitButton) {
    submitButton.textContent = 'Send Inquiry';
    const submitStatus = document.createElement('p');
    submitStatus.className = 'consultation-submit-status';
    submitStatus.setAttribute('role', 'status');
    submitStatus.setAttribute('aria-live', 'polite');
    submitStatus.hidden = true;

    const privacy = document.createElement('p');
    privacy.className = 'consultation-privacy';
    privacy.textContent = 'We will get back to you within 1–2 business days. Your information is safe with us and will never be shared.';
    submitButton.after(submitStatus, privacy);
  }

  return buildDetailsSection(form);
}

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-consultation-turnstile]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.consultationTurnstile = '';
    script.addEventListener('load', () => resolve(window.turnstile));
    script.addEventListener('error', reject);
    document.head.append(script);
  });
}

let activeTurnstileWidget = null;

async function getTurnstileToken(container, siteKey) {
  if (!siteKey) return '';
  const turnstile = await loadTurnstile();

  // A Turnstile token is single-use. Remove/reset the previous widget before
  // creating a new one so repeated submissions do not reuse a stale widget.
  if (activeTurnstileWidget !== null) {
    try {
      turnstile.remove(activeTurnstileWidget);
    } catch {
      try { turnstile.reset(activeTurnstileWidget); } catch { /* no-op */ }
    }
    activeTurnstileWidget = null;
  }
  container.replaceChildren();

  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('Verification timed out'));
    }, 30000);
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      callback(value);
    };

    activeTurnstileWidget = turnstile.render(container, {
      sitekey: siteKey,
      size: 'invisible',
      callback: (token) => finish(resolve, token),
      'error-callback': () => finish(reject, new Error('Verification failed')),
      'expired-callback': () => finish(reject, new Error('Verification expired')),
    });
    turnstile.execute(activeTurnstileWidget);
  });
}

function resetTurnstile() {
  if (activeTurnstileWidget === null || !window.turnstile) return;
  try { window.turnstile.reset(activeTurnstileWidget); } catch { /* no-op */ }
}

function initializeConsultationForm() {
  const form = document.querySelector(CONSULTATION_FORM_SELECTOR);
  if (!form || form.dataset.consultationFormReady === 'true') return;

  form.id = form.id || 'consultation-form';
  form.dataset.consultationFormReady = 'true';
  form.action = getConsultationApiUrl();
  form.method = 'POST';
  form.enctype = 'multipart/form-data';

  const honeypot = addHiddenField(form, 'website', '');
  honeypot.type = 'text';
  honeypot.tabIndex = -1;
  honeypot.autocomplete = 'off';
  honeypot.setAttribute('aria-hidden', 'true');
  honeypot.style.display = 'none';

  const upload = enhanceConsultationForm(form);
  const fileInput = upload?.fileInput || form.querySelector('input[type="file"][name="reference"], input[type="file"][name="attachment"]');
  if (fileInput) {
    fileInput.name = 'attachment';
    // Attachments are optional. Remove a stale required attribute that may
    // exist in an older copy of the Shopify page template.
    fileInput.removeAttribute('required');
    fileInput.removeAttribute('aria-required');
    fileInput.accept = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/quicktime,video/webm';

    const uploadField = upload?.uploadZone || fileInput.parentElement;
    const hint = upload?.hint;

    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      const invalid = isInvalidAttachment(file);

      // An invalid file is optional, so discard it immediately. Otherwise the
      // browser keeps it in input.files and checkValidity() blocks a later
      // submission even after the visitor thinks they cancelled the upload.
      if (invalid) fileInput.value = '';
      fileInput.setCustomValidity('');
      uploadField?.classList.toggle('has-file', Boolean(file && !invalid));
      uploadField?.classList.toggle('has-error', invalid);
      if (upload?.fileStatus && upload?.fileName) {
        upload.fileStatus.hidden = !file || invalid;
        upload.fileName.textContent = file && !invalid ? file.name : '';
      }
      if (hint) hint.textContent = invalid
        ? 'Please choose a supported image or video up to 10 MB.'
        : 'JPG, PNG, WEBP, HEIC, MP4, MOV · up to 10 MB';
      if (invalid) fileInput.reportValidity();
    });

    // Some browsers do not fire a change event when the file picker is
    // cancelled. Clear a previously rejected file before opening it again.
    fileInput.addEventListener('click', () => {
      if (isInvalidAttachment(fileInput.files?.[0])) fileInput.value = '';
    });
  }

  window.toggleTypeOptions = () => {
    const type = getSelectedRadioValue(form, 'jewelryType');
    setConditionalSection('engagementOptions', type === 'Engagement Ring');
    setConditionalSection('ringStyleOptions', type === 'Ring');
  };

  window.toggleStoneOptions = () => {
    const type = getSelectedRadioValue(form, 'stoneType');
    const hasLooseStone = type === 'Diamond' || type === 'Gemstone';
    setConditionalSection('stoneOriginOptions', hasLooseStone);
    setConditionalSection('diamondOptions', type === 'Diamond');
    setConditionalSection('gemstoneOptions', type === 'Gemstone');
    setConditionalSection('engravedOptions', type === 'Engraved');
    setConditionalSection('shapeOptions', hasLooseStone);
  };

  window.toggleTypeOptions();
  window.toggleStoneOptions();

  let isSubmitting = false;
  let submissionTimer;
  const turnstileContainer = document.createElement('div');
  turnstileContainer.style.position = 'absolute';
  turnstileContainer.style.left = '-9999px';
  form.append(turnstileContainer);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const missingSelection = getMissingSelection(form);
    if (missingSelection) {
      showFormMessage(form, `Please select ${missingSelection[1]}.`);
      form.querySelector(`input[name="${missingSelection[0]}"]`)?.closest('.section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (fileInput && !fileInput.checkValidity()) {
      fileInput.reportValidity();
      return;
    }

    const submitButton = form.querySelector('button[type="submit"]');
    const submitStatus = form.querySelector('.consultation-submit-status');
    const originalButtonText = submitButton?.textContent || 'Send Inquiry';
    const submissionId = crypto.randomUUID();
    const controller = new AbortController();
    submissionTimer = window.setTimeout(() => controller.abort(), SUBMISSION_TIMEOUT);
    isSubmitting = true;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      const spinner = document.createElement('span');
      spinner.className = 'consultation-submit-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.textContent = 'Sending...';
      submitButton.replaceChildren(spinner, label);
    }
    if (submitStatus) {
      submitStatus.hidden = false;
      submitStatus.textContent = 'Verifying securely and sending your inquiry…';
    }

    try {
      const formData = new FormData(form);
      formData.set('submission_id', submissionId);
      formData.set('source_url', window.location.href);
      formData.delete('_url');
      formData.delete('_subject');
      formData.delete('_template');
      formData.delete('_captcha');
      formData.delete('_honey');
      formData.set('turnstileToken', await getTurnstileToken(turnstileContainer, configValue('turnstileSiteKey', '')));
      if (submitStatus) submitStatus.textContent = 'Saving your inquiry…';

      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: { 'X-Idempotency-Key': submissionId },
        signal: controller.signal,
        credentials: 'omit',
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to submit your inquiry. Please try again.');

      form.reset();
      fileInput?.setCustomValidity('');
      upload?.uploadZone?.classList.remove('has-file', 'has-error');
      const uploadHint = form.querySelector('.consultation-upload-hint');
      if (uploadHint) uploadHint.textContent = 'JPG, PNG, WEBP, HEIC, MP4, MOV · up to 10 MB';
      if (upload?.fileStatus) upload.fileStatus.hidden = true;
      window.toggleTypeOptions();
      window.toggleStoneOptions();
      showFormMessage(form, 'Thank you. Your inquiry has been received successfully. We will contact you soon.', 'success');
    } catch (error) {
      const message = error?.name === 'AbortError'
        ? 'The request timed out. Please check your connection and try again.'
        : error instanceof Error ? error.message : 'Unable to submit your inquiry. Please try again.';
      showFormMessage(form, message);
    } finally {
      window.clearTimeout(submissionTimer);
      resetTurnstile();
      isSubmitting = false;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
        submitButton.removeAttribute('aria-busy');
      }
      if (submitStatus) {
        submitStatus.hidden = true;
        submitStatus.textContent = '';
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeConsultationForm, { once: true });
} else {
  initializeConsultationForm();
}
